import { AxiosResponse } from 'axios';
import {
  Order,
  OrderBasicInfo,
  OrderRateInfo,
  OrderToSelect,
} from '../../models/order';
import { OrderDetail } from '../../models/order-detail';
import { api } from '../../providers/base-api';
import { PaginatedResponse } from '../../utils/types';
import {
  ChangeOrderPricesRequestData,
  BulkUpdateWithdrawalTypeData,
  ClientApprovalOrderDeadlineResponseDto,
  ClientCancelOrderResponse,
  CloneOrderRequestData,
  CreateOrderByXBZData,
  CreateOrderData,
  CreateOrderDetailsData,
  CreateReworkOrder,
  FinancialApprovalOrderData,
  GetByCloneOrderResponse,
  GetByReworkOrderResponse,
  GetOrderGoalMetricsParams,
  GetOrderPcpRequestQuery,
  GetOrderPcpResponse,
  GetOrdersToDispatchResponse,
  GetOrderToSelectParams,
  OrderChangeDeadlineRequestData,
  OrderChangePCPRequestData,
  OrderMetricGoals,
  OrderNightApprovalRequestData,
  OrderProceedWithProblem,
  OrdersLatePaginatedParams,
  OrdersPaginatedParams,
  OrdersPaginatedResponse,
  OrdersTransfersPaginatedResponse,
  RateOrderData,
  ReleaseToProductionRequestData,
  RequestOrderSampleRequestData,
  SetOrderChangeDispatchData,
  SetOrderDispatchmentData,
  SetOrderFinancialNoteRequestData,
  SetOrderFinishedData,
  SetOrderFinishPackProblem,
  SetOrderPausedData,
  SetOrderReceivementData,
  SetOrderToDispatchResquestData,
  UpdateOrderData,
  UpdateOrderDatesData,
  UpdateOrderStatusData,
} from '../../utils/types/data/services/orders';
import {
  UpdateOrderPriceCheckedData,
  UpdateOrderMinData,
  ReportOrderPriceProblemData,
} from '../../utils/types/data/services/orders';

export async function paginatedOrdersRequest(
  params: OrdersPaginatedParams,
): Promise<OrdersPaginatedResponse> {
  const response = await api.get<OrdersPaginatedResponse>('orders', { params });

  return response.data;
}

export async function paginatedOrdersFullRequest(
  params: OrdersPaginatedParams,
): Promise<AxiosResponse<OrdersPaginatedResponse>> {
  return api.get<OrdersPaginatedResponse>('orders', { params });
}

export async function getOrdersPriceConferenceRequest(
  params: OrdersPaginatedParams,
): Promise<AxiosResponse<OrdersPaginatedResponse>> {
  return api.get<OrdersPaginatedResponse>('orders/price-conference', {
    params,
  });
}

export async function paginatedOrdersTransfersRequest(
  params: OrdersPaginatedParams,
): Promise<AxiosResponse<OrdersTransfersPaginatedResponse>> {
  const response = await api.get<OrdersTransfersPaginatedResponse>(
    'orders/list/transfer',
    {
      params,
    },
  );

  return response;
}

export async function paginatedHistoricOrdersRequest(
  params: OrdersPaginatedParams,
): Promise<OrdersPaginatedResponse> {
  const response = await api.get<OrdersPaginatedResponse>(
    'orders/get-historic',
    { params },
  );

  return response.data;
}

export async function createOrdersRequest(
  data: CreateOrderData,
): Promise<AxiosResponse<Order>> {
  const response = await api.post<Order>('orders', data);

  return response;
}

export async function createReworkOrderRequest(
  data: CreateReworkOrder,
): Promise<Order> {
  const response = await api.post<Order>('orders/rework', data);

  return response.data;
}

export async function createOrderDetailsRequest(
  orderId: number,
  data: CreateOrderDetailsData,
): Promise<OrderDetail> {
  const response = await api.post<OrderDetail>(
    `orders/${orderId}/detail`,
    data,
  );

  return response.data;
}

export async function getOrderRequest(
  id: number,
): Promise<AxiosResponse<Order>> {
  const response = await api.get<Order>(`orders/${id}`);

  return response;
}

export async function getOrderDetailsRequest(id: number): Promise<OrderDetail> {
  const response = await api.get<OrderDetail>(`orders/${id}/detail`);

  return response.data;
}

export async function updateOrderDatesRequest(
  id: number,
  data: UpdateOrderDatesData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/dates`, data);

  return response.data;
}

export async function updateOrderStatusRequest(
  id: number,
  data: UpdateOrderStatusData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/status`, data);

  return response.data;
}

export async function changeOrderStatusRequest(
  id: number,
  data: UpdateOrderStatusData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/${id}/status`, data);

  return response;
}

export async function setOrderReceivementRequest(
  id: number,
  data: SetOrderReceivementData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/receivement`, data);

  return response.data;
}

export async function setOrderDispatchmentRequest(
  id: number,
  data: SetOrderDispatchmentData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/dispatchment`, data);

  return response.data;
}

export async function updateOrderRequest(
  id: number,
  data: UpdateOrderData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}`, data);

  return response.data;
}

export async function updateOrderDetailsRequest(
  id: number,
  data: CreateOrderDetailsData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/detail`, data);

  return response.data;
}

export async function setOrderFinishedRequest(
  id: number,
  data: SetOrderFinishedData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/finish`, data);

  return response.data;
}

export async function setOrderFinishedReceptionRequest(
  id: number,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/set-finished`);

  return response.data;
}

export async function setOrderDispatchDataRequest(
  id: number,
  data: SetOrderChangeDispatchData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/change-dispatch`, data);

  return response.data;
}

export async function setOrderProductionFinishedRequest(
  id: number,
): Promise<AxiosResponse<Order>> {
  const response = await api.put<Order>(`orders/production-finish/${id}`);

  return response;
}

export async function setOrderPackingFinishedRequest(
  id: number,
): Promise<Order> {
  const response = await api.put<Order>(`orders/packing-finish/${id}`);

  return response.data;
}

export async function setOrderPausedRequest(
  id: number,
  data: SetOrderPausedData,
): Promise<Order> {
  const response = await api.put<Order>(`orders/${id}/pause`, data);

  return response.data;
}

export async function setOrderFinancialNote(
  id: number,
  data: SetOrderFinancialNoteRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/set-financial-note/${id}`, data);

  return response;
}

export async function changeOrderPricesRequest(
  id: number,
  data: ChangeOrderPricesRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/set-prices/${id}`, data);

  return response;
}

export async function getOrderPCPRequest(
  query: GetOrderPcpRequestQuery,
): Promise<AxiosResponse<GetOrderPcpResponse>> {
  const response = await api.get<GetOrderPcpResponse>(`orders/get-pcp`, {
    params: query,
  });

  return response;
}

export async function getOrdersToDispatchRequest(
  hash: string,
): Promise<AxiosResponse<GetOrdersToDispatchResponse[]>> {
  const response = await api.get<GetOrdersToDispatchResponse[]>(
    `orders-dispatch/${hash}`,
  );

  return response;
}

export async function setOrdersToDispatchRequest(
  data: SetOrderToDispatchResquestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders-dispatch`, data);

  return response;
}

export async function paginatedOrdersLateRequest(
  params: OrdersLatePaginatedParams,
): Promise<OrdersPaginatedResponse> {
  const response = await api.get<OrdersPaginatedResponse>('orders/get-late', {
    params,
  });

  return response.data;
}

export async function setOrderPackingFinishedProblemRequest(
  id: number,
  data: SetOrderFinishPackProblem,
): Promise<Order> {
  const response = await api.put<Order>(`orders/set-pack-problem/${id}`, data);

  return response.data;
}

export async function orderProceedWithProblemRequest(
  id: number,
  data: OrderProceedWithProblem,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(
    `orders/proceed-with-problem/${id}`,
    data,
  );

  return response;
}

export async function getOrdersGoalMetrics(
  query: GetOrderGoalMetricsParams,
): Promise<AxiosResponse<OrderMetricGoals>> {
  const response = await api.get<OrderMetricGoals>(`orders/goal-metrics`, {
    params: query,
  });

  return response;
}

export async function releaseProductionRequest(
  id: number,
  data: ReleaseToProductionRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/${id}/release-production`, data);

  return response;
}

export async function requestSampleRequest(
  id: number,
  data: RequestOrderSampleRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`orders/${id}/request-sample`, data);

  return response;
}

export async function cloneOrderRequest(
  id: number,
  data: CloneOrderRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`orders/${id}/clone`, data);

  return response;
}

export async function getByCloneOrderRequest(
  id: number,
): Promise<AxiosResponse<GetByCloneOrderResponse>> {
  const response = await api.get<GetByCloneOrderResponse>(
    `orders/${id}/by-clone`,
  );

  return response;
}

export async function getByReworkOrderRequest(
  id: number,
): Promise<AxiosResponse<GetByReworkOrderResponse>> {
  const response = await api.get<GetByReworkOrderResponse>(
    `orders/${id}/by-rework`,
  );

  return response;
}

export async function getOrdersToSelect(
  query: GetOrderToSelectParams,
): Promise<AxiosResponse<PaginatedResponse<OrderToSelect>>> {
  const response = await api.get<PaginatedResponse<OrderToSelect>>(
    `orders/to-select`,
    {
      params: query,
    },
  );

  return response;
}

export async function clientCancelOrderRequest(
  id: number,
): Promise<AxiosResponse<ClientCancelOrderResponse>> {
  const response = await api.delete<ClientCancelOrderResponse>(
    `orders/${id}/client-cancel`,
  );

  return response;
}

export async function orderNightApprovalRequest(
  id: number,
  data: OrderNightApprovalRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/${id}/night-approval`, data);

  return response;
}

export async function getOrderBasicInfo(
  id: number,
): Promise<AxiosResponse<OrderBasicInfo>> {
  const response = await api.get<OrderBasicInfo>(`orders/${id}/minimal`);

  return response;
}

export async function approvalOrderDeadlineRequest(
  id: number,
  approval: boolean,
): Promise<AxiosResponse<ClientApprovalOrderDeadlineResponseDto>> {
  const response = await api.put<ClientApprovalOrderDeadlineResponseDto>(
    `orders/${id}/deadline-approval`,
    { approval },
  );

  return response;
}

export async function changeOrderDeadlineRequest(
  id: number,
  data: OrderChangeDeadlineRequestData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/deadline/${id}/change`, data);

  return response;
}

export async function getOrderRateInfoRequest(
  id: number,
): Promise<AxiosResponse<OrderRateInfo>> {
  const response = await api.get<OrderRateInfo>(`orders/rating/${id}/info`);

  return response;
}

export async function rateOrderRequest(
  id: number,
  data: RateOrderData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`orders/rating/${id}`, data);

  return response;
}

export async function paginatedOrdersFinancialApprovalRequest(
  params: OrdersPaginatedParams,
): Promise<AxiosResponse<OrdersPaginatedResponse>> {
  const response = await api.get<OrdersPaginatedResponse>(
    'orders/financial-approval',
    { params },
  );

  return response;
}

export async function financialApprovalOrderRequest(
  id: number,
  data: FinancialApprovalOrderData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/${id}/financial-approval`, data);

  return response;
}

export async function paginatedOrdersHandlingReportRequest(
  params: OrdersPaginatedParams,
): Promise<AxiosResponse<OrdersPaginatedResponse>> {
  const response = await api.get<OrdersPaginatedResponse>(
    'orders/report/handling',
    { params },
  );

  return response;
}

export async function changeOrderPCPRequest(
  id: number,
  data: OrderChangePCPRequestData,
): Promise<AxiosResponse<Order>> {
  const response = await api.put<Order>(`orders/change/pcp/${id}`, data);

  return response;
}

export async function createOrdersByXBZRequest(
  data: CreateOrderByXBZData,
): Promise<AxiosResponse<Order>> {
  const response = await api.post<Order>('orders/create/xbz', data);

  return response;
}

export async function updateOrderMinRequest(
  id: number,
  data: UpdateOrderMinData,
): Promise<AxiosResponse<Order>> {
  const response = await api.patch<Order>(`orders/min/${id}`, data);

  return response;
}

export async function bulkUpdateWithdrawalTypeRequest(
  data: BulkUpdateWithdrawalTypeData,
): Promise<AxiosResponse<void>> {
  const response = await api.patch<void>('orders/bulk/withdrawal-type', data);

  return response;
}

export async function updateOrderPriceCheckedRequest(
  id: number,
  data: UpdateOrderPriceCheckedData,
): Promise<AxiosResponse<void>> {
  const response = await api.put<void>(`orders/${id}/price-checked`, data);

  return response;
}

export async function reportOrderPriceProblemRequest(
  id: number,
  data: ReportOrderPriceProblemData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`orders/${id}/price-problem`, data);

  return response;
}

export async function getLastOrderByServiceRequest(params: {
  companyId: number;
  serviceId: number;
  currentOrderId?: number;
}): Promise<AxiosResponse<Order>> {
  const response = await api.get<Order>('orders/last-by-service', { params });

  return response;
}
